import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getHostels, getHostelAdmissions, generateInvoice, bulkGenerateInvoices, getInvoices, getInvoice,
  getBillingConfig, getAttendanceHistory, getMealsHistory, getHostelAdmissionOptions, getHostelPayments,
  getBillingAdjustments, addBillingAdjustment, deleteBillingAdjustment, getResidentHistoryByCnic,
} from '../api';
import { printIndividualInvoice, viewIndividualInvoice, printBulkInvoice, exportIndividualInvoiceCSV, exportBulkInvoiceCSV } from '../utils/invoicePrint';
import { FiFileText, FiPrinter, FiEye, FiDownload, FiUsers, FiCheckSquare, FiSquare, FiUser } from 'react-icons/fi';
import { usePermissions } from '../utils/permissions';
import ResidentProfileModal from './ResidentProfileModal';

const firstOfMonth = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Invoices() {
  const { can } = usePermissions();
  const canCreate = can('hostel_invoices', 'create');
  const [hostels, setHostels] = useState([]);
  const [hostelId, setHostelId] = useState('');
  const [mode, setMode] = useState('individual'); // individual | bulk
  const [admissions, setAdmissions] = useState([]);
  const [admissionId, setAdmissionId] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [periodStart, setPeriodStart] = useState(firstOfMonth());
  const [periodEnd, setPeriodEnd] = useState(todayStr());
  const [config, setConfig] = useState(null);
  const [includeAttendance, setIncludeAttendance] = useState(false);
  const [includeMeals, setIncludeMeals] = useState(false);
  const [includeDaysSummary, setIncludeDaysSummary] = useState(true);
  const [includeMealsSummary, setIncludeMealsSummary] = useState(false);
  const [regenerate, setRegenerate] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [campuses, setCampuses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [profileRecord, setProfileRecord] = useState(null);

  useEffect(() => {
    getHostelAdmissionOptions().then(res => {
      setCampuses(res.data.campuses || []);
      setCourses(res.data.courses || []);
    }).catch(() => {});
  }, []);

  const admissionsById = useMemo(() => Object.fromEntries(admissions.map(a => [a.id, a])), [admissions]);
  const viewProfile = (admissionId, fallbackName) => {
    setProfileRecord(admissionsById[admissionId] || { id: admissionId, full_name: fallbackName });
  };

  useEffect(() => {
    getHostels().then(res => {
      setHostels(res.data);
      if (res.data.length && !hostelId) setHostelId(res.data[0].id);
    }).catch(() => setError('Failed to load hostels'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAdmissions = useCallback(async () => {
    if (!hostelId) return;
    try {
      const res = await getHostelAdmissions({ hostel_id: hostelId, status: 'active' });
      const list = Array.isArray(res.data) ? res.data : (res.data.admissions || []);
      setAdmissions(list);
      if (list.length && !admissionId) setAdmissionId(list[0].id);
      setSelectedIds(list.map(a => a.id));
    } catch (e) { /* non-fatal */ }
    try {
      const cRes = await getBillingConfig(hostelId);
      setConfig(cRes.data);
    } catch (e) { /* non-fatal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostelId]);

  const loadHistory = useCallback(async () => {
    if (!hostelId) return;
    setLoadingHistory(true);
    try {
      const res = await getInvoices({ hostel_id: hostelId });
      setHistory(res.data);
    } catch (e) { /* non-fatal */ }
    setLoadingHistory(false);
  }, [hostelId]);

  useEffect(() => { loadAdmissions(); loadHistory(); }, [loadAdmissions, loadHistory]);

  const toggleResident = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleAll = () => {
    setSelectedIds(prev => prev.length === admissions.length ? [] : admissions.map(a => a.id));
  };

  const fetchDetailRows = async () => {
    const attendanceRows = includeAttendance
      ? (await getAttendanceHistory({ hostel_id: hostelId, start: periodStart, end: periodEnd })).data
      : [];
    const mealRows = (includeMeals || includeMealsSummary)
      ? (await getMealsHistory({ hostel_id: hostelId, start: periodStart, end: periodEnd })).data
      : [];
    return { attendanceRows, mealRows };
  };

  const handleGenerateIndividual = async (format) => {
    if (!admissionId) { setError('Select a resident'); return; }
    setGenerating(true); setError(''); setSuccess('');
    try {
      const res = await generateInvoice({ admission_id: admissionId, period_start: periodStart, period_end: periodEnd, force: regenerate });
      const full = await getInvoice(res.data.id);
      const { attendanceRows, mealRows } = await fetchDetailRows();
      const admAtt = attendanceRows.filter(r => String(r.admission_id) === String(admissionId));
      const admMeals = mealRows.filter(r => String(r.admission_id) === String(admissionId));
      const printOpts = { attendanceRows: admAtt, mealRows: admMeals, includeAttendance, includeMeals, includeDaysSummary, includeMealsSummary };
      if (format === 'csv') {
        exportIndividualInvoiceCSV(full.data, printOpts);
      } else {
        printIndividualInvoice(full.data, printOpts);
      }
      setSuccess(res.data.reused ? `Using existing invoice ${res.data.invoice_number} for this period` : `Invoice ${res.data.invoice_number} generated`);
      loadHistory();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to generate invoice');
    }
    setGenerating(false);
  };

  const handleGenerateBulk = async (format) => {
    if (!selectedIds.length) { setError('Select at least one resident'); return; }
    setGenerating(true); setError(''); setSuccess('');
    try {
      const res = await bulkGenerateInvoices({ hostel_id: hostelId, admission_ids: selectedIds, period_start: periodStart, period_end: periodEnd, force: regenerate });
      if (!res.data.invoices.length) { setError('No residents to invoice for this period'); setGenerating(false); return; }
      const h = res.data.invoices[0].hostel;
      const hostelName = h?.name || hostels.find(x => String(x.id) === String(hostelId))?.name || 'Hostel';
      const { attendanceRows, mealRows } = await fetchDetailRows();
      const printOpts = {
        hostelName, period_start: periodStart, period_end: periodEnd,
        invoices: res.data.invoices, grand_total: res.data.grand_total,
        attendanceRows, mealRows, includeAttendance, includeMeals, includeDaysSummary, includeMealsSummary,
        showMeals, showDeduction,
      };
      if (format === 'csv') {
        exportBulkInvoiceCSV(printOpts);
      } else {
        printBulkInvoice({ ...printOpts, hostelProfileImage: h?.profile_image });
      }
      setSuccess(`${res.data.invoices.length} invoice(s) generated — grand total PKR ${res.data.grand_total.toLocaleString()}`);
      loadHistory();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to generate invoices');
    }
    setGenerating(false);
  };

  const reprint = async (id) => {
    try {
      const res = await getInvoice(id);
      printIndividualInvoice(res.data);
    } catch (e) {
      setError('Failed to load invoice');
    }
  };

  const viewInvoiceById = async (id) => {
    try {
      const res = await getInvoice(id);
      viewIndividualInvoice(res.data);
    } catch (e) {
      setError('Failed to load invoice');
    }
  };

  const showAttendanceOption = config?.attendance_tracking_enabled;
  const showMealsOption = config?.meals_tracking_enabled;
  const showMeals = !!(config?.meals_tracking_enabled && config?.meals_affect_billing);
  const showDeduction = !!(config?.billing_mode === 'monthly_flat' && config?.attendance_tracking_enabled && config?.attendance_affects_billing);

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title"><FiFileText /> Hostel Invoices</h2>
          <p style={{ color: 'var(--text-muted)', margin: '4px 0 0' }}>Generate individual or consolidated invoices for any period, tenure or session</p>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="form-row" style={{ marginBottom: '10px' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Hostel</label>
          <select className="form-control" value={hostelId} onChange={e => { setHostelId(e.target.value); setAdmissionId(''); }}>
            {hostels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Period Start</label>
          <input type="date" className="form-control" value={periodStart} onChange={e => setPeriodStart(e.target.value)} />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Period End</label>
          <input type="date" className="form-control" value={periodEnd} max={todayStr()} onChange={e => setPeriodEnd(e.target.value)} />
        </div>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', cursor: 'pointer', marginBottom: '14px', color: 'var(--text-secondary)' }}>
        <input type="checkbox" checked={regenerate} onChange={e => setRegenerate(e.target.checked)} />
        Regenerate (issue a fresh invoice for this period instead of reusing an existing one — use after correcting attendance/meals)
      </label>

      <div style={{ marginBottom: '14px' }}>
        <label style={{ fontWeight: 600, fontSize: '12.5px', marginBottom: '8px', display: 'block', color: 'var(--text-secondary)' }}>Fields to include in export</label>
        <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={includeDaysSummary} onChange={e => setIncludeDaysSummary(e.target.checked)} />
            Present / Absent / Total days (columns)
          </label>
          {showMealsOption && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: 'var(--text-secondary)' }}>
              <input type="checkbox" checked={includeMealsSummary} onChange={e => setIncludeMealsSummary(e.target.checked)} />
              Meals served — breakfast/lunch/dinner counts (columns)
            </label>
          )}
          {showAttendanceOption && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: 'var(--text-secondary)' }}>
              <input type="checkbox" checked={includeAttendance} onChange={e => setIncludeAttendance(e.target.checked)} />
              Full day-by-day attendance log
            </label>
          )}
          {showMealsOption && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: 'var(--text-secondary)' }}>
              <input type="checkbox" checked={includeMeals} onChange={e => setIncludeMeals(e.target.checked)} />
              Full day-by-day meal log
            </label>
          )}
        </div>
      </div>

      {canCreate && (
      <>
      <div className="action-buttons" style={{ marginBottom: '16px' }}>
        <button className={`btn btn-small ${mode === 'individual' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setMode('individual')}>Individual Invoice</button>
        <button className={`btn btn-small ${mode === 'bulk' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setMode('bulk')}><FiUsers /> Bulk / Selected Residents</button>
      </div>

      {mode === 'individual' ? (
        <div className="form-row" style={{ alignItems: 'flex-end' }}>
          <div className="form-group" style={{ marginBottom: 0, gridColumn: 'span 2' }}>
            <label>Resident</label>
            <select className="form-control" value={admissionId} onChange={e => setAdmissionId(e.target.value)}>
              {admissions.length === 0 && <option value="">No active residents</option>}
              {admissions.map(a => <option key={a.id} value={a.id}>{a.full_name}{a.cnic ? ` — ${a.cnic}` : ''}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0, display: 'flex', gap: '8px' }}>
            <button className="btn btn-primary" onClick={() => handleGenerateIndividual('pdf')} disabled={generating || !admissionId}>
              <FiPrinter /> {generating ? 'Generating...' : 'Generate & Print'}
            </button>
            <button className="btn btn-secondary" onClick={() => handleGenerateIndividual('csv')} disabled={generating || !admissionId}>
              <FiDownload /> CSV
            </button>
          </div>
        </div>
      ) : (
        <div>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '10px' }}>
            Select the residents in this hostel to include, then export a single consolidated table for the period.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <button className="btn btn-secondary btn-small" onClick={toggleAll}>
              {selectedIds.length === admissions.length ? <FiCheckSquare /> : <FiSquare />} {selectedIds.length === admissions.length ? 'Unselect All' : 'Select All'}
            </button>
            <span style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>{selectedIds.length} of {admissions.length} selected</span>
          </div>
          <div style={{ maxHeight: '220px', overflowY: 'auto', border: '1px solid var(--border-light)', borderRadius: '8px', padding: '6px', marginBottom: '14px' }}>
            {admissions.length === 0 ? (
              <p style={{ fontSize: '12.5px', padding: '8px', color: 'var(--text-muted)' }}>No active residents.</p>
            ) : admissions.map(a => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', flex: 1 }}>
                  <input type="checkbox" checked={selectedIds.includes(a.id)} onChange={() => toggleResident(a.id)} />
                  {a.full_name}{a.cnic ? ` — ${a.cnic}` : ''}
                </label>
                <button className="btn btn-small btn-secondary" title="View resident profile" onClick={() => viewProfile(a.id, a.full_name)}><FiUser size={12} /></button>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-primary" onClick={() => handleGenerateBulk('pdf')} disabled={generating || !selectedIds.length}>
              <FiPrinter /> {generating ? 'Generating...' : 'Generate & Print PDF'}
            </button>
            <button className="btn btn-secondary" onClick={() => handleGenerateBulk('csv')} disabled={generating || !selectedIds.length}>
              <FiDownload /> {generating ? 'Generating...' : 'Export CSV'}
            </button>
          </div>
        </div>
      )}
      </>
      )}

      <div style={{ marginTop: '30px' }}>
        <h3 style={{ fontSize: '15px', marginBottom: '12px', color: 'var(--text-primary)' }}>Invoice History</h3>
        {loadingHistory ? (
          <div className="loading"><div className="spinner"></div></div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Invoice #</th><th>Resident</th><th>Period</th><th>Total</th><th>Generated</th><th>Action</th></tr></thead>
              <tbody>
                {history.length === 0 ? (
                  <tr><td colSpan="6" style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)' }}>No invoices generated yet for this hostel.</td></tr>
                ) : history.map(inv => (
                  <tr key={inv.id}>
                    <td>{inv.invoice_number}</td>
                    <td>
                      {inv.full_name}{' '}
                      <button className="btn btn-small btn-secondary" title="View resident profile" onClick={() => viewProfile(inv.admission_id, inv.full_name)}><FiUser size={12} /></button>
                    </td>
                    <td>{new Date(inv.period_start).toLocaleDateString('en-PK')} – {new Date(inv.period_end).toLocaleDateString('en-PK')}</td>
                    <td><strong>PKR {Number(inv.total_amount).toLocaleString()}</strong></td>
                    <td>{new Date(inv.created_at).toLocaleDateString('en-PK')}</td>
                    <td style={{ display: 'flex', gap: '6px' }}>
                      <button className="btn btn-secondary btn-small" onClick={() => viewInvoiceById(inv.id)}><FiEye /> View</button>
                      <button className="btn btn-secondary btn-small" onClick={() => reprint(inv.id)}><FiPrinter /> Print</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {profileRecord && (
        <ResidentProfileModal
          admission={profileRecord}
          campuses={campuses}
          courses={courses}
          onClose={() => setProfileRecord(null)}
          fetchAttendance={(id) => getAttendanceHistory({ admission_id: id })}
          fetchMeals={(id) => getMealsHistory({ admission_id: id })}
          fetchPayments={(id) => getHostelPayments({ admission_id: id })}
          fetchInvoices={(id) => getInvoices({ admission_id: id })}
          fetchInvoiceDetail={(id) => getInvoice(id)}
          fetchAdjustments={(id) => getBillingAdjustments({ admission_id: id })}
          fetchResidentHistory={(cnic) => getResidentHistoryByCnic(cnic)}
          canManageAdjustments
          onAddAdjustment={addBillingAdjustment}
          onDeleteAdjustment={deleteBillingAdjustment}
        />
      )}
    </div>
  );
}
